import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  useTheme,
  alpha,
} from '@mui/material';
import { useAIDTMediaQuery } from '../../hooks';

export interface RowInfoTableProps {
  data: Array<{
    id: string;
    label: string | React.ReactNode;
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    multiline?: boolean;
    rows?: number;
  }>;
  disabled?: boolean;
  borderColor?: string;
  labelBackgroundColor?: string;
  labelTextColor?: string;
  rowBorderColor?: string;
  rowBorderRightColor?: string;
}

const RowInfoTable = ({
  data,
  disabled = false,
  borderColor,
  labelBackgroundColor,
  labelTextColor,
  rowBorderColor,
  rowBorderRightColor,
}: RowInfoTableProps): JSX.Element => {
  const theme = useTheme();
  const { isTablet } = useAIDTMediaQuery();

  // 색상 계산 (primary 계열 기준, props로 오버라이드 가능)
  const primaryColor = theme.palette.primary.main;
  const finalBorderColor = borderColor || primaryColor;
  const finalLabelBackgroundColor =
    labelBackgroundColor || alpha(primaryColor, 0.1);
  const finalLabelTextColor = labelTextColor || primaryColor;
  const finalRowBorderColor = rowBorderColor || primaryColor;
  const finalRowBorderRightColor =
    rowBorderRightColor || alpha(primaryColor, 0.3);

  return (
    <Table
      sx={{
        width: '100%',
        tableLayout: 'fixed',
        border: `1px solid ${finalBorderColor}`,
        borderRadius: '4px',
        borderCollapse: 'separate',
        borderSpacing: 0,
        overflow: 'hidden',
        '& .MuiTableCell-root': {
          borderRight: `1px solid ${finalBorderColor}`,
          borderBottom: `1px solid ${finalBorderColor}`,
        },
        '& .MuiTableRow-root:last-of-type .MuiTableCell-root': {
          borderBottom: 'none',
        },
        '& .MuiTableCell-root:last-of-type': {
          borderRight: 'none',
        },
      }}
    >
      <TableBody>
        {data.map((row, index) => {
          const isMultiline =
            row.multiline !== undefined ? row.multiline : true;
          const actualRows = row.rows || 1;

          return (
            <TableRow
              key={row.id}
              sx={{
                borderRight: `1px solid ${finalRowBorderRightColor}`,
                borderLeft: `1px solid ${finalRowBorderColor}`,
                borderTop: `1px solid ${finalRowBorderColor}`,
                borderBottom:
                  index < data.length - 1
                    ? `1px solid ${finalRowBorderColor}`
                    : 'none',
              }}
            >
              {/* 왼쪽 라벨 셀 */}
              <TableCell
                sx={{
                  width: isTablet ? '35%' : '30%',
                  backgroundColor: finalLabelBackgroundColor,
                  padding: '0.5rem 1rem',
                  verticalAlign: 'middle',
                  textAlign: 'center',
                }}
              >
                {typeof row.label === 'string' ? (
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: finalLabelTextColor,
                      wordBreak: 'keep-all',
                      whiteSpace: 'normal',
                    }}
                  >
                    {row.label}
                  </Typography>
                ) : (
                  row.label
                )}
              </TableCell>

              {/* 오른쪽 입력 셀 */}
              <TableCell
                sx={{
                  width: isTablet ? '85%' : '70%',
                  backgroundColor: 'transparent',
                  padding: '0.5rem',
                  verticalAlign: 'middle',
                }}
              >
                <TextField
                  fullWidth
                  value={row.value}
                  onChange={e => row.onChange(e.target.value)}
                  placeholder={row.placeholder}
                  disabled={disabled}
                  multiline={isMultiline}
                  minRows={actualRows}
                  maxRows={actualRows > 1 ? actualRows : 2}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      border: 'none',
                      backgroundColor: '#fff',
                      overflow: 'auto',
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover fieldset': {
                        border: 'none',
                      },
                      '&.Mui-focused fieldset': {
                        border: 'none',
                      },
                    },
                    '& .MuiInputBase-input': {
                      color: '#1E1E1E',
                      fontFamily: theme.typography.subtitle1.fontFamily,
                      fontSize: theme.typography.subtitle1.fontSize,
                      fontWeight: theme.typography.subtitle1.fontWeight,
                      lineHeight: theme.typography.subtitle1.lineHeight,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                    },
                  }}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default RowInfoTable;

